// colDef/placementColDef.tsx
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  PlacementRecordStatus,
  PlacementRecordType,
} from "@/services/placement.service";
import { formatDate } from "@/utils/formatDate.util";
import { createColumnHelper } from "@tanstack/react-table";
import { Pencil, Trash2 } from "lucide-react";

const columnHelper = createColumnHelper<PlacementRecordType>();

export type PlacementActionType = {
  id: string;
  name: string; // This might be role or company now
  action: "edit" | "delete";
};

// ─── Status pill config ───────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  PlacementRecordStatus,
  { label: string; className: string }
> = {
  confirmed: {
    label: "Confirmed",
    className: "bg-gray-900 text-white",
  },
  offer_pending: {
    label: "Offer Pending",
    className: "bg-gray-100 text-gray-600",
  },
  rejected: {
    label: "Rejected",
    className: "bg-red-100 text-red-600",
  },
};

export const createPlacementColumns = (
  onActionClick: (payload: PlacementActionType) => void
) => [
  // Role — bold
  columnHelper.accessor("role", {
    header: "Role",
    cell: (info) => (
      <span className="text-sm font-medium">{info.getValue()}</span>
    ),
  }),

  // Company — ID (Ideally populated name)
  columnHelper.accessor("company", {
    header: "Company",
    cell: (info) => (
      <span className="text-sm font-medium">{info.getValue()}</span>
    ),
  }),

  // Students — Count
  columnHelper.accessor("students", {
    header: "Students",
    cell: (info) => (
      <span className="text-sm">{info.getValue().length} Student(s)</span>
    ),
  }),

  // Departments — Joined
  columnHelper.accessor("departments", {
    header: "Dept",
    cell: (info) => (
      <div className="flex flex-wrap gap-1">
        {info
          .getValue()
          .slice(0, 2)
          .map((dept, i) => (
            <span
              key={i}
              className="rounded-md bg-gray-900 px-2 py-0.5 text-xs font-bold text-white"
            >
              {dept}
            </span>
          ))}
        {info.getValue().length > 2 && (
          <span className="text-xs text-gray-500">
            +{info.getValue().length - 2}
          </span>
        )}
      </div>
    ),
  }),

  // Package — green $
  columnHelper.accessor("package", {
    header: "Package",
    cell: (info) => (
      <span className="font-semibold text-green-600">
        ${info.getValue().toLocaleString()}
      </span>
    ),
  }),

  // Job Type
  columnHelper.accessor("jobType", {
    header: "Type",
    cell: (info) => <span className="text-sm">{info.getValue()}</span>,
  }),

  // Placement Date — YYYY-MM-DD
  columnHelper.accessor("placementDate", {
    header: "Date",
    cell: (info) => (
      <span className="whitespace-nowrap text-sm">
        {formatDate(info.getValue(), { locale: "en-CA", dateStyle: "short" })}
      </span>
    ),
  }),

  // Status pill
  columnHelper.accessor("status", {
    header: "Status",
    cell: (info) => {
      const cfg = STATUS_CONFIG[info.getValue()];
      return (
        <span
          className={cn(
            "whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold",
            cfg.className
          )}
        >
          {cfg.label}
        </span>
      );
    },
  }),

  // Inline edit / delete
  columnHelper.display({
    id: "actions",
    header: "Actions",
    cell: (info) => {
      const data = info.row.original;
      return (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() =>
              onActionClick({
                id: data._id,
                name: data.role,
                action: "edit",
              })
            }
            aria-label="Edit placement"
          >
            <Pencil size={15} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() =>
              onActionClick({
                id: data._id,
                name: data.role,
                action: "delete",
              })
            }
            aria-label="Delete placement"
          >
            <Trash2 size={15} />
          </Button>
        </div>
      );
    },
  }),
];
